import { GRADERS } from "@/lib/catalog/graders";
import { GRADE_FLOORS, kindFacets, meetsGradeFloor } from "@/lib/filters/kindFacets";
import { isKind, type Kind, kindOf } from "@/lib/inventory/kind";
import { CONDITION_ORDER } from "@/lib/money/conditionMarket";
import { toCents } from "@/lib/money/money";

import type { PublicBrowseItem, PublicBrowseSetFacet } from "@/hooks/usePublicBrowse";

/**
 * How the customer browse list (#468) is ordered. `featured` is the server's own order (relevance
 * or recency as the backend chose it) and is the default, so an untouched list keeps that order.
 * The other three are the price and name sorts: a buyer looking for the cheapest copy, or scanning
 * alphabetically by name.
 */
export type BrowseSort = "featured" | "price-asc" | "price-desc" | "name";

/**
 * The distinct games present in the loaded browse set (#630), in first-seen order, which feed the
 * per-location game filter chips. A vendor carrying one game (or none the catalog matched) yields
 * at most one entry, so the screen shows no chip row; a multi-game table yields one chip per game.
 */
export function distinctGames(items: PublicBrowseItem[]): string[] {
  const seen: string[] = [];
  for (const it of items) {
    if (it.game && !seen.includes(it.game)) seen.push(it.game);
  }
  return seen;
}

/**
 * Every facet the customer can narrow a browse page by. An absent or null field does not
 * constrain, and an empty `conditions` list does not either.
 *
 * - `stockGroup` is the Location the unit is filed in (TRO-1095), matched on the group's name.
 * - `conditions` admits a unit whose condition is any listed code ("NM", "LP", …).
 * - `minCents` and `maxCents` bound the shown price inclusively. An unpriced unit fails any bound,
 *   since it has no price to fall inside it.
 * - `grader` and `gradeMin` narrow slabs (`kindFacets`): a slab by that grader, a grade at or
 *   above that floor.
 */
export type BrowseFilters = {
  game?: string | null;
  set?: PublicBrowseSetFacet | null;
  stockGroup?: string | null;
  /** Raw, Graded or Sealed, the one split every filter offers (`lib/inventory/kind`, TRO-1101). */
  kind?: Kind | null;
  conditions?: readonly string[];
  minCents?: number | null;
  maxCents?: number | null;
  grader?: string | null;
  gradeMin?: string | null;
};

const priceCents = (it: PublicBrowseItem): number | null =>
  it.price == null || it.price === "" ? null : toCents(it.price);

/**
 * The Filters sheet's refinements beyond game and set (TRO-1095), as the screen holds them. Price
 * bounds stay the dollar text the customer typed, so a half-typed "1." is not rewritten under them;
 * `refinementFilters` turns them into cents.
 */
export type BrowseRefinements = {
  kind: Kind | null;
  conditions: string[];
  minPrice: string;
  maxPrice: string;
  grader: string | null;
  gradeMin: string | null;
};

/**
 * Picks a Kind and clears whichever refinements it cannot carry, per the shared `kindFacets` rule
 * inventory's `kindPatch` follows: Graded drops Condition, anything but Graded drops the slab pair.
 */
export function kindRefinements(kind: Kind | null): Partial<BrowseRefinements> {
  const { condition, slab } = kindFacets(kind);
  return {
    kind,
    ...(condition ? {} : { conditions: [] }),
    ...(slab ? {} : { grader: null, gradeMin: null }),
  };
}

export const NO_REFINEMENTS: BrowseRefinements = {
  kind: null,
  conditions: [],
  minPrice: "",
  maxPrice: "",
  grader: null,
  gradeMin: null,
};

/** Dollar text to cents, or null when blank or not a non-negative number, which does not
 *  constrain: a stray letter in the box should not empty the list. */
export function priceBoundCents(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const dollars = Number(trimmed);
  return Number.isFinite(dollars) && dollars >= 0 ? Math.round(dollars * 100) : null;
}

export function refinementFilters(refinements: BrowseRefinements): BrowseFilters {
  return {
    kind: refinements.kind,
    conditions: refinements.conditions,
    minCents: priceBoundCents(refinements.minPrice),
    maxCents: priceBoundCents(refinements.maxPrice),
    grader: refinements.grader,
    gradeMin: refinements.gradeMin,
  };
}

/** The URL params the refinements persist in, so Back from item detail restores them (TRO-298).
 *  An unset refinement maps to `undefined`, which removes its param. `graded` and `type` are the
 *  pre-TRO-1101 keys for the kind, always cleared so a link carries one spelling. */
export function refinementParams(
  refinements: BrowseRefinements,
): Record<string, string | undefined> {
  return {
    kind: refinements.kind ?? undefined,
    graded: undefined,
    type: undefined,
    cond: refinements.conditions.length ? refinements.conditions.join(",") : undefined,
    min: refinements.minPrice.trim() || undefined,
    max: refinements.maxPrice.trim() || undefined,
    grader: refinements.grader ?? undefined,
    grade: refinements.gradeMin ?? undefined,
  };
}

/** Reads `refinementParams` back. An unknown value reads as unset, so a hand-edited or stale link
 *  degrades to fewer filters rather than an empty list. A link from before TRO-1101 still reads:
 *  `graded=raw|graded` and `type=sealed` name a kind, and `type=single` (raw or graded) no kind. */
export function refinementsFromParams(
  params: Partial<
    Record<
      "kind" | "graded" | "cond" | "type" | "min" | "max" | "grader" | "grade",
      string | undefined
    >
  >,
): BrowseRefinements {
  const legacy = params.type === "sealed" ? "sealed" : params.graded;
  const kind = isKind(params.kind) ? params.kind : isKind(legacy) ? legacy : null;
  // A facet the Type does not offer (`kindFacets`) would narrow with no control to see or clear
  // it, so a link carrying one for another Type reads it as unset.
  const applies = kindFacets(kind);
  return {
    kind,
    // Known codes only: the server refuses an unknown condition (TRO-1856), so a stale `cond=EX`
    // would fail the whole table rather than narrow it.
    conditions: applies.condition && params.cond
      ? params.cond.split(",").filter((c) => CONDITION_ORDER.some((known) => known === c))
      : [],
    minPrice: params.min ?? "",
    maxPrice: params.max ?? "",
    grader: applies.slab ? GRADERS.find((g) => g === params.grader) ?? null : null,
    gradeMin: applies.slab
      ? GRADE_FLOORS.find((g) => g.value === params.grade)?.value ?? null
      : null,
  };
}

/** Whether `filters` narrows anything, so the header reads "N of total" rather than the total. */
export function hasBrowseFilters(filters: BrowseFilters): boolean {
  const { game, set, stockGroup, kind, conditions, minCents, maxCents, grader, gradeMin } = filters;
  return [game, set, stockGroup, kind, minCents, maxCents, grader, gradeMin].some((v) => v != null)
    || !!conditions?.length;
}

/** A facet a server may narrow in SQL and lists in `server_facets` (TRO-2347). */
export type ServerFacet = "game" | "set";

/**
 * The facets the browse endpoint narrows in SQL (TRO-1856), as its query parameters. They mirror
 * `matches` below, so the client re-applying them to the server's rows changes nothing.
 *
 * Game and set go too, but only those in `served`, the `server_facets` a page of this server listed
 * (TRO-2347). Before that a show table from before them would apply `game` to a different game
 * than the client's, so an unlisted facet is the client's alone and the hooks walk for it. A set
 * always travels with its game, which the server requires.
 */
export function browseFacetParams(
  filters: BrowseFilters,
  served: readonly string[] = [],
): [string, string][] {
  const { game, set, stockGroup, kind, conditions, grader, gradeMin } = filters;
  const params: [string, string][] = [];
  if (stockGroup) params.push(["location", stockGroup]);
  if (kind) params.push(["kind", kind]);
  for (const condition of conditions ?? []) params.push(["condition", condition]);
  if (grader) params.push(["grader", grader]);
  if (gradeMin) params.push(["grade_min", gradeMin]);
  const sendsSet = !!set && served.includes("set") && served.includes("game");
  const facetGame = (served.includes("game") ? game : null) ?? (sendsSet ? set.game : null);
  if (facetGame) params.push(["game", facetGame]);
  if (sendsSet) params.push(["set", set.name]);
  return params;
}

/** The game and set facets `filters` sets that `served` (the `server_facets` the request was sent
 *  under, see `browseFacetParams`) does not cover, which the client must therefore apply itself
 *  over every page of the table. */
export function clientFacets(
  filters: BrowseFilters,
  served: readonly string[] = [],
): ServerFacet[] {
  const wanted: ServerFacet[] = [];
  if (filters.game != null) wanted.push("game");
  if (filters.set != null) wanted.push("set");
  return wanted.filter((facet) => !served.includes(facet));
}

/** Whether `filters` sets a facet only the client can apply, which therefore needs every page of
 *  the table loaded first: game and set, unless `served` (the `server_facets` the pages were
 *  requested under, TRO-2347) says the server narrowed them. A price bound is the server's where it reports
 *  `server_price_sort` (TRO-2346); elsewhere the hooks walk for it themselves. */
export function needsWholeTable(filters: BrowseFilters, served: readonly string[] = []): boolean {
  return clientFacets(filters, served).length > 0;
}

/** Whether `filters` bounds the price at either end. */
export function hasPriceBounds(filters: Pick<BrowseFilters, "minCents" | "maxCents">): boolean {
  return filters.minCents != null || filters.maxCents != null;
}

function matches(item: PublicBrowseItem, filters: BrowseFilters): boolean {
  const { game, set, stockGroup, kind, conditions, minCents, maxCents, grader, gradeMin } = filters;
  if (game && item.game !== game) return false;
  if (grader && !(item.is_graded && item.grading_company?.toUpperCase() === grader)) return false;
  if (gradeMin && !(item.is_graded && meetsGradeFloor(item.grade, gradeMin))) return false;
  if (set && (item.game !== set.game || item.set_name !== set.name)) return false;
  if (stockGroup && item.stock_group_name !== stockGroup) return false;
  if (kind && kindOf(item) !== kind) return false;
  if (conditions?.length && !(item.condition && conditions.includes(item.condition))) return false;
  if (minCents != null || maxCents != null) {
    const cents = priceCents(item);
    if (cents === null) return false;
    if (minCents != null && cents < minCents) return false;
    if (maxCents != null && cents > maxCents) return false;
  }
  return true;
}

/**
 * Narrow and sort a browse page on the client: the two decisions the storefront still makes itself
 * now that text search runs on the server (#768). Pure and self-contained so the ordering rules
 * (especially unpriced items last) are unit-testable without mounting the screen.
 *
 * - Game chip (#630): a per-location narrowing over `game`, which the wire shape carries on every
 *   row, so the server need not decide it again. An unset game does not constrain.
 * - Set facet (TRO-290): a narrowing over the catalog set, matched on the `(game, set_name)` pair
 *   the facet endpoint keys its sets by, never on the name alone. Set names are only unique within
 *   a game (Pokémon and Magic each print a "Promos"), so a name-only match would admit another
 *   game's set of the same name. An unset facet does not constrain.
 * - Location, kind (Raw / Graded / Sealed, TRO-1101), condition and price (TRO-1095): see
 *   `BrowseFilters`. All but price are also sent to the server (`browseFacetParams`), so here they
 *   only re-confirm its rows.
 * - Sort: `featured` returns the filtered order untouched (`Array.prototype.sort` is stable in
 *   Hermes and V8, so the price and name sorts also keep the server's order among ties). Both price
 *   sorts put unpriced items (`null` or empty `price`) last in either direction: an item with no
 *   asking price has nothing to rank by and should not read as cheapest or most expensive. With
 *   `serverOrdered`, the server already ordered the pages by price (TRO-2346), so a price sort
 *   keeps their order too.
 *
 * This does not re-run the customer's typed query. `usePublicBrowse` sends it as `q` and the
 * server answers it (#768), because filtering here over the loaded pages made a card past those
 * pages read as a false "no matches". Re-testing the server's answer here could only contradict it,
 * because the two sides read different fields: the server matches the stored
 * `tcg_product.collector_number` (#794), while a row's rendered `number` is resolved per page from
 * the catalog and can be absent for the same unit.
 */
export function filterAndSortBrowse(
  items: PublicBrowseItem[],
  sort: BrowseSort,
  filters: BrowseFilters = {},
  { serverOrdered = false }: { serverOrdered?: boolean; } = {},
): PublicBrowseItem[] {
  const filtered = items.filter((item) => matches(item, filters));

  if (sort === "featured" || (serverOrdered && sort !== "name")) return filtered;

  const sorted = [...filtered];
  if (sort === "name") {
    sorted.sort((a, b) => a.product_name.localeCompare(b.product_name));
    return sorted;
  }

  sorted.sort((a, b) => {
    const av = priceCents(a);
    const bv = priceCents(b);
    if (av === null && bv === null) return 0;
    if (av === null) return 1; // unpriced sinks, both directions
    if (bv === null) return -1;
    return sort === "price-asc" ? av - bv : bv - av;
  });
  return sorted;
}
